/**
 * The data-table look, in one place.
 *
 * Manage Coupon Codes and Search Shopify Orders sit behind two tabs of the same page, so a
 * different header tone or row density between them reads as a bug rather than a style. The
 * Shopify orders table is the reference; these are its classes, named so the coupon table can
 * wear them too.
 *
 * Shared as strings rather than a component because the two are built on different primitives —
 * the coupon table on shadcn `Table`, the orders table on a plain `<table>` — and the point is
 * that they LOOK identical, not that they share an implementation.
 */

/**
 * Scroll container. Fills the height its parent grants and scrolls inside, so the page does not.
 *
 * `[&>div]:overflow-visible` is what makes a sticky header actually stick when the table is built
 * on shadcn `Table`. That primitive wraps its `<table>` in its own `relative w-full overflow-auto`
 * div, with NO height bound — so that div is a scroll container that can never scroll, and it is
 * the sticky `<thead>`'s nearest scrolling ancestor. The header then has nothing to stick to: the
 * page scrolls the whole box away, column names included. Neutralising the inner overflow hands
 * that role back to THIS element, which is bounded.
 *
 * A no-op for a table built on a plain `<table>` (the Shopify orders one) — there is no child div
 * to match — so the two shapes can keep wearing the same class.
 *
 * The parent must give this a definite height, or `h-full` resolves to auto and nothing sticks.
 * The house pattern is `flex min-h-0 flex-1 flex-col` on the region that holds it.
 */
export const DATA_TABLE_WRAPPER =
  "h-full overflow-auto rounded-md border [&>div]:overflow-visible";

/**
 * Header row. Sticky so column names survive scrolling, with an OPAQUE background — `bg-muted/50`
 * lets rows show through as they pass underneath, which is what the transparency bug looked like.
 */
export const DATA_TABLE_HEADER = "sticky top-0 z-10 bg-muted text-left";

/** Row separators + hover. */
export const DATA_TABLE_ROW = "border-t hover:bg-muted/40";

/**
 * Cell density, applied once on the table element rather than on every cell.
 *
 * The descendant selectors are deliberate: the coupon table has 13 columns and dozens of cells, so
 * setting padding per `TableCell` would be a large edit that the next new column would silently
 * skip. Headers also get `text-foreground` — shadcn's `TableHead` defaults to muted, which reads
 * washed out next to the orders table.
 */
export const DATA_TABLE_CELLS =
  "[&_th]:px-2.5 [&_th]:py-2 [&_th]:font-medium [&_th]:text-foreground [&_td]:px-2.5 [&_td]:py-2";
